import { useState, useRef, useEffect } from 'react';

const faqs = [
  {
    q: '沒有音樂基礎也可以學古箏嗎？',
    a: '完全可以。這裡大多數學員都是從零開始的。我們不預設你有任何音樂背景，從認識聲音、熟悉樂器開始，一步一步慢慢來。',
  },
  {
    q: '成人現在開始學會太晚嗎？',
    a: '不會太晚。學音樂沒有年齡限制，只有你是否願意給自己這個機會。成人學習有自己的優勢——更清楚自己想要什麼，也更能享受過程本身。',
  },
  {
    q: '孩子適合這樣的學習方式嗎？',
    a: '如果你的孩子不喜歡被催促、不需要高壓競賽環境，這裡很適合。我們重視孩子在學習中是否感到快樂、安心，而不只是彈得多快、多準。',
  },
  {
    q: '一開始一定要買古箏嗎？',
    a: '不需要。剛開始可以先用課室的練習琴上課，感受一段時間後，再決定是否購買屬於自己的琴。我也會在適當時機協助你選擇最合適的款式。',
  },
  {
    q: '加入 LINE@ 之後一定要報名嗎？',
    a: '完全不會有任何壓力。加入 LINE@ 只是一個開始聊聊的方式，你可以問問題、了解教學方式，聊完覺得不適合也完全沒關係，不會有任何強迫成交的情形。',
  },
  {
    q: '課程頻率可以怎麼安排？',
    a: '基本建議是每週一次，但我們可以依照你的生活狀態彈性調整。偶爾需要暫停、調整上課時間，都可以事先溝通。',
  },
];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#e0d8cc]">
      <button
        className="w-full flex items-center justify-between py-5 text-left cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <span className="text-[1.1875rem] font-medium text-[#2c2420] leading-[1.6] pr-4">{q}</span>
        <div className="w-7 h-7 flex items-center justify-center shrink-0">
          <i className={`${open ? 'ri-subtract-line' : 'ri-add-line'} text-[#9a8470] text-lg transition-all duration-200`}></i>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-48 pb-5' : 'max-h-0'}`}>
        <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62]">{a}</p>
      </div>
    </div>
  );
}

export default function FaqSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* Left */}
          <div className={`lg:col-span-2 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="divider-gold"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">FAQ</span>
            </div>
            <h2
              className="font-serif-tc text-[#2c2420] leading-[1.4] mb-6"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
            >
              常見問題
            </h2>
            <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62] mb-8">
              有任何疑問都可以在這裡找到初步的解答。如果還有其他想了解的，歡迎加入 LINE@ 直接問我。
            </p>
            <a
              href="https://line.me"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#7a6248] text-[#7a6248] text-[1rem] font-medium hover:bg-[#7a6248] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-line-fill text-[#06C755]"></i>
              加入 LINE@ 諮詢
            </a>
          </div>

          {/* Right */}
          <div className={`lg:col-span-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.15s' }}>
            {faqs.slice(0, 3).map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
